import {
  IPosition,
} from '../types';

type TZoomCallback = (zoomDelta: number, anchorPosition: IPosition) => void;

/**
 * Configuration options for the editor view.
 * @property zoomSpeed - Speed at which zooming occurs.
 * @property minZoom - Minimum allowed zoom level.
 * @property maxZoom - Maximum allowed zoom level.
 */
export interface IZoomOptions {
  zoomSpeed?: number,
}

export interface IUseZoomOptions {
  zoomCallback: TZoomCallback,
  options?: IZoomOptions,
}

const DEFAULT_ZOOM_OPTIONS: Required<IZoomOptions> = {
  zoomSpeed: 0.1,
};

export type TUseZoom = {
  activateZoom: (zoomContainerOrId: string | HTMLElement) => void,
  deactivateZoom: () => void,
};

export function useZoom({
  zoomCallback,
  options,
}: IUseZoomOptions): TUseZoom {
  let zoomContainer: HTMLElement | null = null;
  const settings = Object.assign({}, DEFAULT_ZOOM_OPTIONS, options);

  let requestAnimationFrameId: number | undefined;

  /**
   * Handles scroll wheel events.
   * @param event - Wheel event.
   */
  function handleScrollEvent(event: WheelEvent) {
    event.preventDefault();

    // To reduce load on the browser a limit the drag updates to exactly one update
    // per browser paint
    if (requestAnimationFrameId !== undefined) {
      cancelAnimationFrame(requestAnimationFrameId);
    }
    requestAnimationFrameId = requestAnimationFrame(() => {
      let zoomDelta = 0;
      if (event.deltaY > 0) {
        zoomDelta = settings.zoomSpeed;
      } else if (event.deltaY < 0) {
        zoomDelta = -settings.zoomSpeed;
      }

      const anchor = {
        x: 0,
        y: 0,
      };
      if (zoomContainer !== null) {
        const zoomRect = zoomContainer.getBoundingClientRect();
        // Calculate mouse position relative to the container's viewport
        anchor.x = event.clientX - zoomRect.left;
        anchor.y = event.clientY - zoomRect.top;
      }

      zoomCallback(zoomDelta, anchor);
    });
  }

  /**
   * Activates zoom event listening.
   */
  function activateZoom(zoomContainerOrId: string | HTMLElement) {
    zoomContainer = null;
    if (typeof zoomContainerOrId === 'string') {
      zoomContainer = document.getElementById(zoomContainerOrId);
    } else {
      zoomContainer = zoomContainerOrId;
    }
    if (!zoomContainer) {
      console.warn(`Could not find zoom container id='${zoomContainerOrId}'. Skip zoom activation.`);
      return;
    }
    zoomContainer.addEventListener('wheel', handleScrollEvent);
  }

  /**
   * Deactivates zoom event listening.
   */
  function deactivateZoom() {
    zoomContainer?.removeEventListener('wheel', handleScrollEvent);
    zoomContainer = null;
  }

  return {
    activateZoom,
    deactivateZoom,
  };
}
